// WHAT: Form component for entering/editing Yazio credentials
// WHY:  Separates form logic from main component to reduce complexity
// DOMAIN: Settings → Yazio
// DEPENDENCIES: React, @/components/ui, lucide-react

import { CheckCircle, Key, Loader2, Settings, X } from "lucide-react";
import React from "react";

import { Button, Card, Heading, Input, Label, Stack } from "@/components/ui";

interface YazioCredentialsFormProperties {
  /** Whether credentials are already configured */
  isConfigured: boolean;
  /** Whether form is in loading state */
  loading: boolean;
  /** Handler for cancel action */
  onCancel: () => void;
  /** Handler for password change */
  onPasswordChange: (value: string) => void;
  /** Handler for save action */
  onSave: () => void;
  /** Handler for username change */
  onUsernameChange: (value: string) => void;
  /** Password input value */
  password: string;
  /** Whether form is in saving state */
  saving: boolean;
  /** Username input value */
  username: string;
}

/**
 * WHAT:
 *   Form component for entering/editing Yazio credentials.
 *
 * WHY:
 *   Separates form UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.isConfigured
 *
 * @param root0.loading
 *
 * @param root0.password
 *
 * @param root0.saving
 *
 * @param root0.username
 *
 * @param root0.onCancel
 *
 * @param root0.onPasswordChange
 *
 * @param root0.onSave
 *
 * @param root0.onUsernameChange
 *
 * @returns {JSX.Element} Yazio credentials form component
 *
 */
export const YazioCredentialsForm: React.FC<YazioCredentialsFormProperties> = ({
  isConfigured,
  loading,
  onCancel,
  onPasswordChange,
  onSave,
  onUsernameChange,
  password,
  saving,
  username,
}) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <Stack align="center" direction="row" gap={3}>
          <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
            <Settings className="w-5 h-5 text-tracq-bright-blue" />
          </div>
          <Heading className="text-lg font-medium text-white" level={3}>
            {isConfigured ? "Update Yazio Credentials" : "Configure Yazio Credentials"}
          </Heading>
        </Stack>

        {/* Username Field */}
        <Stack direction="col" gap={2}>
          <Label className="text-slate-300 flex items-center gap-2">
            <Key className="w-4 h-4" />
            <span>Username</span>
            <span className="text-red-400">*</span>
          </Label>
          <Input
            className="w-full"
            disabled={loading || saving}
            onChange={(e) => {
              onUsernameChange(e.target.value);
            }}
            placeholder="Enter your Yazio username..."
            size="md"
            type="text"
            value={username}
          />
        </Stack>

        {/* Password Field */}
        <Stack direction="col" gap={2}>
          <Label className="text-slate-300 flex items-center gap-2">
            <Key className="w-4 h-4" />
            <span>Password</span>
            <span className="text-red-400">*</span>
          </Label>
          <Input
            className="w-full"
            disabled={loading || saving}
            onChange={(e) => {
              onPasswordChange(e.target.value);
            }}
            placeholder="Enter your Yazio password..."
            size="md"
            type="password"
            value={password}
          />
        </Stack>

        {/* Action Buttons */}
        <Stack className="flex-wrap" direction="row" gap={2}>
          <Button
            disabled={loading || saving || !username.trim() || !password.trim()}
            onClick={() => {
              onSave();
            }}
            size="md"
            variant="primary"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {isConfigured ? "Update Credentials" : "Save Credentials"}
              </>
            )}
          </Button>

          {isConfigured ? (
            <Button disabled={saving} onClick={onCancel} size="md" variant="ghost">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
};

